import { createStore } from "zustand/vanilla";
import { persist, PersistOptions } from "zustand/middleware";
import { User, List, SyncStatus } from "@/types";
import { updateUserAction } from "@/app/actions/user";
import { addCompaniesToList, deleteListAction, upsertListAction } from "@/app/actions/list";
import { v4 as uuid4 } from "uuid";
import { CompanyCSVFields } from "@/components/modals/CreateListModal";

export type UserState = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  secondaryEmail: string | null;
  avatar: string | null;
  isPrimaryEmailVerified: boolean;
  isSecondaryEmailVerified: boolean;
  loginServices: { apple: boolean; google: boolean };
  lists: Record<string, List[]>;
  pylonHashedEmail: string;
};

export type UserActions = {
  getFullName: () => string;
  updateUser: (user: Partial<User>, options?: { path?: string }) => Promise<void>;
  getListsByWorkspaceId: (workspaceId?: string) => List[];
  createOrEditList: (listData: {
    id?: string;
    name: string;
    crmProvider?: "hubspot" | "salesforce" | null;
    externalListId?: string | null;
    color: string;
    visibility: "workspace" | "private" | "restricted";
    isAutomated: boolean;
    workspaceId: string | null;
    isDefault: boolean;
    createdBy: { id: string; name: string };
    members?: { id: string; fullName: string; avatarUrl?: string }[];
    companies: CompanyCSVFields[] | null;
    personaId?: string | null;
    syncStatus?: SyncStatus;
  }) => Promise<string>;
  deleteList: (listId: string) => Promise<void>;
  updateListOrder: (workspaceId: string, newOrder: List[]) => void;
  updateListSyncStatus: (listId: string, syncStatus: SyncStatus, workspaceId?: string) => void;
};

export type UserStore = UserState & UserActions;

export const defaultInitState: UserState = {
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  secondaryEmail: null,
  avatar: null,
  isPrimaryEmailVerified: false,
  isSecondaryEmailVerified: false,
  loginServices: {
    apple: false,
    google: false,
  },
  lists: {
    personal: [],
  },
  pylonHashedEmail: "",
};

export const createUserStore = (initState: UserState = defaultInitState) =>
  createStore<UserStore>()(
    persist(
      (set, get) => ({
        ...initState,

        getFullName: () => `${get().firstName} ${get().lastName}`,

        updateUser: async (user: Partial<User>, options?: { path?: string }) => {
          const currentUser = get();
          set(() => ({ ...currentUser, ...user }));

          try {
            const result = await updateUserAction({
              firstName: user.firstName,
              lastName: user.lastName,
              avatarUrl: user.avatar,
              email: user.email,
              secondaryEmail: user.secondaryEmail,
              path: options?.path,
            });

            localStorage.setItem("user-update", Date.now().toString());

            if (result.error) {
              set(() => currentUser);
              throw new Error(result.error);
            }
          } catch (error) {
            set(() => currentUser);
            throw error;
          }
        },

        getListsByWorkspaceId: workspaceId => {
          const lists = get().lists;
          return workspaceId ? (lists[workspaceId] ?? []) : (lists.personal ?? []);
        },

        updateListSyncStatus: (listId: string, syncStatus: SyncStatus, workspaceId?: string) => {
          const isPersonal = !workspaceId;
          const listKey = isPersonal ? "personal" : workspaceId!;

          set(state => ({
            ...state,
            lists: {
              ...state.lists,
              [listKey]: state.lists[listKey].map(list =>
                list.id === listId ? { ...list, syncStatus } : list
              ),
            },
          }));
        },
        createOrEditList: async listData => {
          const workspaceId = listData.workspaceId;
          const isPersonal = !workspaceId;
          const listKey = isPersonal ? "personal" : workspaceId!;
          const isEdit = !!listData.id;

          const tempId = uuid4();
          const optimisticId = isEdit ? listData.id! : tempId;

          const newList = {
            ...listData,
            id: optimisticId,
          } as List;

          // optimistically add or update the list
          set(state => {
            const currentLists = state.lists[listKey] ?? [];
            const updatedLists = isEdit
              ? currentLists.map(l => (l.id === optimisticId ? newList : l))
              : [...currentLists, newList];

            return {
              ...state,
              lists: {
                ...state.lists,
                [listKey]: updatedLists,
              },
            };
          });

          const formattedList = {
            ...listData,
            members: listData.members?.map(m => m.id),
          };

          const result = await upsertListAction({
            ...formattedList,
            id: isEdit ? listData.id : undefined,
          });

          const finalListId = result.data ?? tempId;

          if (result.error) {
            // rollback
            set(state => ({
              ...state,
              lists: {
                ...state.lists,
                [listKey]: (state.lists[listKey] ?? []).filter(list => list.id !== optimisticId),
              },
            }));
            throw new Error(result.error);
          }

          if (!isEdit) {
            // update the temp id to the real id we get back
            set(state => ({
              ...state,
              lists: {
                ...state.lists,
                [listKey]: (state.lists[listKey] ?? []).map(list =>
                  list.id === tempId ? { ...list, id: finalListId } : list
                ),
              },
            }));

            if (listData.isAutomated && (listData.companies?.length ?? 0) > 0) {
              let addResult;
              try {
                // add companies to the workspace_list_companies table
                addResult = await addCompaniesToList({
                  listId: finalListId,
                  companies: listData.companies ?? [],
                  workspaceId: workspaceId ?? "",
                });
              } catch (error) {
                console.error("Error in addCompaniesToList:", error);
                addResult = { error: error };
              }

              if (addResult.error) {
                // delete the newly created list
                await deleteListAction({ listId: finalListId });

                // rollback client state
                set(state => ({
                  ...state,
                  lists: {
                    ...state.lists,
                    [listKey]: (state.lists[listKey] ?? []).filter(list => list.id !== finalListId),
                  },
                }));

                throw new Error(String(addResult.error));
              }
            }
          }
          return finalListId;
        },

        deleteList: async listId => {
          const workspaceKey = Object.entries(get().lists).find(([, lists]) =>
            lists.some(list => list.id === listId)
          )?.[0];

          if (!workspaceKey) throw new Error("List not found");

          const currentLists = get().lists[workspaceKey] ?? [];
          const deletedList = currentLists.find(list => list.id === listId);
          if (!deletedList) throw new Error("List not found in state");

          set(state => ({
            ...state,
            lists: {
              ...state.lists,
              [workspaceKey]: currentLists.filter(list => list.id !== listId),
            },
          }));

          const result = await deleteListAction({ listId });

          if (result.error) {
            set(state => ({
              ...state,
              lists: {
                ...state.lists,
                [workspaceKey]: [...(state.lists[workspaceKey] ?? []), deletedList],
              },
            }));
            throw new Error(result.error);
          }
        },
        updateListOrder: (workspaceId: string, newOrder: List[]) => {
          set(state => {
            const currentLists = state.lists[workspaceId] ?? [];

            const defaultList = currentLists.find(list => list.isDefault);

            return {
              ...state,
              lists: {
                ...state.lists,
                [workspaceId]: defaultList ? [defaultList, ...newOrder] : newOrder,
              },
            };
          });
        },
      }),
      {
        name: "user-store",
        partialize: state => ({
          lists: state.lists,
        }),
        merge: (persistedState, currentState) => {
          const persistedLists = (persistedState as { lists: Record<string, List[]> }).lists ?? {};
          const currentLists = currentState.lists ?? {};

          const mergedLists: Record<string, List[]> = {};

          for (const [workspaceId, currentWorkspaceLists] of Object.entries(currentLists)) {
            const persistedWorkspaceLists = persistedLists[workspaceId] ?? [];

            const currentIds = new Set(currentWorkspaceLists.map(list => list.id));

            const validPersistedLists = (persistedWorkspaceLists ?? []).filter(list =>
              currentIds.has(list.id)
            );

            mergedLists[workspaceId] = [
              ...currentWorkspaceLists,
              ...validPersistedLists.filter(
                persisted => !currentWorkspaceLists.some(cur => cur.id === persisted.id)
              ),
            ];
          }

          return {
            ...currentState,
            lists: mergedLists,
          };
        },
      } as PersistOptions<UserStore, { lists: Record<string, List[]> }>
    )
  );
